/**
 * DemocratCharts — single chart pane within the multi-chart dashboard.
 *
 * Self-contained: own candle data, hover/cursor state, drawings rendering.
 * Drawings are keyed by chartKey so they naturally stay attached to symbols
 * across pane reshuffles. Indicators are global (same set everywhere) in v1.
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import clsx from "clsx";
import { Spinner } from "@/components/ui/Spinner";
import {
  Chart,
  type PriceLineSpec,
  type IndicatorLineSpec,
  type DrawingSpec,
  type DraggedPoint,
  type MoonMarker,
  type SubPaneSeriesSpec,
} from "@/components/chart/Chart";
import { CandleStats } from "@/components/chart/CandleStats";
import { PaneHeader } from "@/components/chart/PaneHeader";
import { useCandles, type Candle } from "@/hooks/useCandles";
import { useTickStream } from "@/hooks/useTickStream";
import { useReplayStore } from "@/store/replay";
import { useTradingStore } from "@/store/trading";
import { useIndicatorsStore, isSubPaneIndicator, isMarkerIndicator } from "@/store/indicators";
import { useDrawingsStore, chartKeyFor, type Drawing } from "@/store/drawings";
import { useDashboardStore, type PaneConfig } from "@/store/dashboard";
import { TIMEFRAME_DEFAULT_DAYS, type SymbolEntry } from "@/lib/symbols";
import { tsToChartTime, formatPrice } from "@/lib/format";
import {
  calculateEMA, calculateSMA, calculateVWAP, calculateBollingerBands,
  calculateMFI, calculateMoonEvents,
} from "@/lib/indicators";

interface ChartPaneProps {
  pane: PaneConfig;
  isActive: boolean;
  replayActiveForThisPane: boolean;
  showCandleStats: boolean;
  compact: boolean;
  onMakeActive?: () => void;
}

export function ChartPane({
  pane, isActive, replayActiveForThisPane, showCandleStats, compact, onMakeActive,
}: ChartPaneProps) {
  const updatePane = useDashboardStore((s) => s.updatePane);
  const setActive = useDashboardStore((s) => s.setActive);
  const { symbol, exchange, symbolName, timeframe } = pane;

  const days = TIMEFRAME_DEFAULT_DAYS[timeframe] ?? 365;
  const chartKey = chartKeyFor(symbol, exchange, timeframe);
  const { data, loading, error } = useCandles({ symbol, exchange, timeframe, days });
  // Live ticks for this pane's symbol (suppressed during replay so the
  // historical bars aren't overwritten by live data).
  const { lastTick, status: streamStatus } = useTickStream(symbol, exchange);
  const liveTick = replayActiveForThisPane ? null : lastTick;

  const [hovered, setHovered] = useState<Candle | null>(null);
  const [cursorPos, setCursorPos] = useState<{ ts: string | null; price: number | null }>({ ts: null, price: null });
  const [draggingDrawing, setDraggingDrawing] = useState(false);

  const { active: replayActive, setupMode, currentIndex } = useReplayStore();
  const effectiveReplay = replayActiveForThisPane && replayActive;

  const {
    drawings: allDrawings, activeTool, pendingPoints, selectedId: selectedDrawingId,
    addPendingPoint, removeDrawing, select, updateTrendPoint, updateHorizontalPrice,
  } = useDrawingsStore();

  const { list: indicatorConfigs } = useIndicatorsStore();
  const { open } = useTradingStore();

  const visibleCandles = useMemo<Candle[]>(() => {
    if (!data?.candles) return [];
    if (effectiveReplay && !setupMode && currentIndex !== null) {
      return data.candles.slice(0, currentIndex + 1);
    }
    return data.candles;
  }, [data, effectiveReplay, setupMode, currentIndex]);

  const currentCandle = visibleCandles[visibleCandles.length - 1] ?? null;

  const displayCandle = useMemo<Candle | null>(() => hovered ?? currentCandle, [hovered, currentCandle]);
  const previousClose = useMemo<number | null>(() => {
    if (!visibleCandles.length || visibleCandles.length < 2 || !displayCandle) return null;
    const idx = visibleCandles.findIndex((c) => c.ts === displayCandle.ts);
    if (idx > 0) return visibleCandles[idx - 1].close;
    return visibleCandles[visibleCandles.length - 2].close;
  }, [visibleCandles, displayCandle]);

  const handleChartClick = useCallback(
    (timeSeconds: number, price: number | null) => {
      if (!isActive) {
        onMakeActive?.();
        setActive(pane.id);
      }
      if (draggingDrawing) return;

      if (replayActiveForThisPane && setupMode && data?.candles) {
        const idx = data.candles.findIndex((c) => tsToChartTime(c.ts) === timeSeconds);
        if (idx >= 0) useReplayStore.getState().setStartIndex(idx);
        return;
      }

      if (activeTool && data?.candles && price !== null) {
        const idx = data.candles.findIndex((c) => tsToChartTime(c.ts) === timeSeconds);
        const ts = idx >= 0 ? data.candles[idx].ts : new Date(timeSeconds * 1000).toISOString();

        if (activeTool === "eraser") {
          const hit = findNearestDrawing(allDrawings, chartKey, timeSeconds, price, data.candles);
          if (hit) removeDrawing(hit);
          return;
        }
        addPendingPoint({ ts, price }, chartKey);
        return;
      }

      if (data?.candles && price !== null) {
        const hit = findNearestDrawing(allDrawings, chartKey, timeSeconds, price, data.candles);
        select(hit);
      }
    },
    [
      isActive, onMakeActive, setActive, pane.id, draggingDrawing, replayActiveForThisPane,
      setupMode, activeTool, data, addPendingPoint, removeDrawing, select, chartKey, allDrawings,
    ]
  );

  const handleDrawingDrag = useCallback(
    (update: DraggedPoint) => {
      const drawing = allDrawings.find((d) => d.id === update.drawingId);
      if (!drawing) return;
      if (drawing.kind === "trend") {
        updateTrendPoint(update.drawingId, update.pointIndex === 0 ? "start" : "end", { ts: update.ts, price: update.price });
      } else if (drawing.kind === "horizontal") {
        updateHorizontalPrice(update.drawingId, update.price);
      }
    },
    [allDrawings, updateTrendPoint, updateHorizontalPrice]
  );

  const priceLines = useMemo<PriceLineSpec[]>(() => {
    if (!open) return [];
    if (open.symbol !== symbol || open.exchange !== exchange) return [];
    const lines: PriceLineSpec[] = [
      {
        id: "entry", price: open.entryPrice,
        color: open.direction === "long" ? "#26a69a" : "#ef5350",
        title: `${open.direction === "long" ? "BUY" : "SELL"} ${open.quantity} @ ${formatPrice(open.entryPrice)}`,
        lineStyle: "solid", lineWidth: 2,
      },
    ];
    if (open.slPrice !== null) lines.push({ id: "sl", price: open.slPrice, color: "#ef5350", title: `SL ${formatPrice(open.slPrice)}`, lineStyle: "dashed", lineWidth: 1 });
    if (open.tpPrice !== null) lines.push({ id: "tp", price: open.tpPrice, color: "#26a69a", title: `TP ${formatPrice(open.tpPrice)}`, lineStyle: "dashed", lineWidth: 1 });
    return lines;
  }, [open, symbol, exchange]);

  const indicators = useMemo<IndicatorLineSpec[]>(() => {
    if (!data?.candles.length) return [];
    const out: IndicatorLineSpec[] = [];
    for (const cfg of indicatorConfigs) {
      if (!cfg.enabled) continue;
      if (isSubPaneIndicator(cfg.type) || isMarkerIndicator(cfg.type)) continue;
      if (cfg.type === "ema") {
        out.push({ id: cfg.id, data: calculateEMA(data.candles, cfg.params.period ?? 21), color: cfg.color, lineWidth: cfg.lineWidth, lineStyle: cfg.lineStyle });
      } else if (cfg.type === "sma") {
        out.push({ id: cfg.id, data: calculateSMA(data.candles, cfg.params.period ?? 50), color: cfg.color, lineWidth: cfg.lineWidth, lineStyle: cfg.lineStyle });
      } else if (cfg.type === "vwap") {
        out.push({ id: cfg.id, data: calculateVWAP(data.candles), color: cfg.color, lineWidth: cfg.lineWidth, lineStyle: cfg.lineStyle });
      } else if (cfg.type === "bb") {
        const bb = calculateBollingerBands(data.candles, cfg.params.period ?? 20, cfg.params.deviation ?? 2);
        out.push({ id: `${cfg.id}_upper`,  data: bb.upper,  color: cfg.color, lineWidth: cfg.lineWidth, lineStyle: cfg.lineStyle });
        out.push({ id: `${cfg.id}_middle`, data: bb.middle, color: cfg.color, lineWidth: cfg.lineWidth, lineStyle: cfg.lineStyle });
        out.push({ id: `${cfg.id}_lower`,  data: bb.lower,  color: cfg.color, lineWidth: cfg.lineWidth, lineStyle: cfg.lineStyle });
      }
    }
    return out;
  }, [data, indicatorConfigs]);

  const subPaneSeries = useMemo<SubPaneSeriesSpec[]>(() => {
    if (!data?.candles.length) return [];
    const out: SubPaneSeriesSpec[] = [];
    for (const cfg of indicatorConfigs) {
      if (!cfg.enabled) continue;
      if (cfg.type === "mfi") {
        out.push({
          id: cfg.id,
          data: calculateMFI(data.candles, cfg.params.period ?? 14),
          color: cfg.color, lineWidth: cfg.lineWidth,
          title: `MFI ${cfg.params.period}`,
          levels: [
            { price: 80, color: "#ef5350", title: "80" },
            { price: 20, color: "#26a69a", title: "20" },
          ],
        });
      }
    }
    return out;
  }, [data, indicatorConfigs]);

  const moonMarkers = useMemo<MoonMarker[]>(() => {
    if (!data?.candles.length) return [];
    const moonEnabled = indicatorConfigs.some((c) => c.type === "moon" && c.enabled);
    if (!moonEnabled) return [];
    return calculateMoonEvents(data.candles).filter((e) => e.kind === "new" || e.kind === "full");
  }, [data, indicatorConfigs]);

  const drawings = useMemo<DrawingSpec[]>(() => {
    return allDrawings
      .filter((d) => d.chartKey === chartKey)
      .map<DrawingSpec>((d) => drawingToSpec(d));
  }, [allDrawings, chartKey]);

  const previewDrawing = useMemo<DrawingSpec | null>(() => {
    if (!isActive || !activeTool || activeTool === "eraser") return null;
    if (!cursorPos.ts || cursorPos.price === null) return null;
    const cursorPoint = { ts: cursorPos.ts, price: cursorPos.price };

    if (activeTool === "horizontal" && pendingPoints.length === 0) {
      return { id: "__preview", kind: "horizontal", price: cursorPos.price, color: "#9598a1", lineWidth: 1 };
    }
    if (activeTool === "trend" && pendingPoints.length === 1) {
      return { id: "__preview", kind: "trend", points: [pendingPoints[0], cursorPoint], color: "#ffb74d", lineWidth: 1 };
    }
    if (activeTool === "fib_retracement" && pendingPoints.length === 1) {
      return { id: "__preview", kind: "fib_retracement", points: [pendingPoints[0], cursorPoint], color: "#9575cd", lineWidth: 1 };
    }
    if (activeTool === "fib_extension") {
      if (pendingPoints.length === 1) {
        return { id: "__preview", kind: "trend", points: [pendingPoints[0], cursorPoint], color: "#9575cd", lineWidth: 1 };
      }
      if (pendingPoints.length === 2) {
        return { id: "__preview", kind: "fib_extension", points: [pendingPoints[0], pendingPoints[1], cursorPoint], color: "#9575cd", lineWidth: 1 };
      }
    }
    if (activeTool === "cycle" && pendingPoints.length === 1) {
      return { id: "__preview", kind: "cycle", points: [pendingPoints[0], cursorPoint], count: 8, color: "#4fc3f7", lineWidth: 1 };
    }
    return null;
  }, [isActive, activeTool, pendingPoints, cursorPos]);

  useEffect(() => {
    if (!isActive && selectedDrawingId) select(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  return (
    <div
      onMouseDown={() => {
        if (!isActive) {
          onMakeActive?.();
          setActive(pane.id);
        }
      }}
      className={clsx(
        "flex h-full min-h-0 min-w-0 flex-col overflow-hidden border bg-bg-panel transition-colors",
        isActive ? "border-accent/60" : "border-border"
      )}
    >
      <PaneHeader
        symbol={symbol}
        exchange={exchange}
        symbolName={symbolName}
        timeframe={timeframe}
        isActive={isActive}
        compact={compact}
        streamStatus={streamStatus}
        hasTicks={lastTick !== null}
        onSymbolChange={(entry: SymbolEntry) =>
          updatePane(pane.id, { symbol: entry.symbol, exchange: entry.exchange, symbolName: entry.name })
        }
        onTimeframeChange={(tf) => updatePane(pane.id, { timeframe: tf })}
      />

      {showCandleStats && (
        <CandleStats candle={displayCandle} previousClose={previousClose} source={data?.source ?? "—"} />
      )}

      <div className="relative flex-1 min-h-0">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-bg-deep/60">
            <Spinner size={compact ? "sm" : "lg"} />
          </div>
        )}
        {error && !loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-2">
            <div className="max-w-md rounded-lg border border-bear/30 bg-bear/10 p-2 text-center text-2xs text-bear">
              {error}
            </div>
          </div>
        )}
        {!loading && !error && data?.candles.length === 0 && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="text-2xs text-text-muted">No data for {symbol} on {timeframe}</div>
          </div>
        )}

        <Chart
          candles={visibleCandles}
          onHover={setHovered}
          onClick={handleChartClick}
          setupCursor={effectiveReplay && setupMode}
          cursor={activeTool && isActive ? "crosshair" : "default"}
          priceLines={priceLines}
          indicators={indicators}
          drawings={drawings}
          moonMarkers={moonMarkers}
          selectedDrawingId={isActive ? selectedDrawingId : null}
          onDrawingDrag={handleDrawingDrag}
          onDrawingDragStart={() => setDraggingDrawing(true)}
          onDrawingDragEnd={() => setTimeout(() => setDraggingDrawing(false), 0)}
          subPaneSeries={subPaneSeries}
          onCursorMove={setCursorPos}
          previewDrawing={previewDrawing}
          liveTick={liveTick}
          watermark={`${symbol} · ${timeframe.toUpperCase()}`}
        />

        {!effectiveReplay && (
          <BarCountdown
            lastTs={data?.candles.length ? data.candles[data.candles.length - 1].ts : null}
            timeframe={timeframe}
          />
        )}
      </div>
    </div>
  );
}

// Bar duration per timeframe (seconds) — countdown shown for intraday only.
const TF_SECONDS: Record<string, number> = {
  "1m": 60, "5m": 300, "15m": 900, "30m": 1800, "1h": 3600, "4h": 14400,
};

/**
 * Countdown to the current bar's close, bottom-right of the chart area.
 * Hides itself when the market is stale (last bar older than one bar duration,
 * i.e. closed market or a dead stream) — no countdown is better than a lie.
 */
function BarCountdown({ lastTs, timeframe }: { lastTs: string | null; timeframe: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const dur = TF_SECONDS[timeframe];
  if (!dur || !lastTs) return null;

  const closeMs = (tsToChartTime(lastTs) + dur) * 1000;
  const rem = Math.floor((closeMs - now) / 1000);
  if (rem <= 0 || rem > dur) return null; // stale bar / closed market

  const mm = Math.floor(rem / 60);
  const ss = rem % 60;
  const text = mm > 0 ? `${mm}:${String(ss).padStart(2, "0")}` : `0:${String(ss).padStart(2, "0")}`;
  const urgent = rem <= 10;

  return (
    <div
      className={clsx(
        "pointer-events-none absolute bottom-8 right-2 z-10 rounded border px-1.5 py-0.5 font-mono text-2xs tabular",
        urgent
          ? "border-warn/40 bg-warn/15 text-warn"
          : "border-border bg-bg-panel/80 text-text-secondary"
      )}
      title="Time to bar close"
    >
      {text}
    </div>
  );
}

function drawingToSpec(d: Drawing): DrawingSpec {
  switch (d.kind) {
    case "trend":
      return { id: d.id, kind: "trend", points: [d.start, d.end], color: d.color, lineWidth: d.lineWidth };
    case "horizontal":
      return { id: d.id, kind: "horizontal", price: d.price, color: d.color, lineWidth: d.lineWidth };
    case "fib_retracement":
      return { id: d.id, kind: "fib_retracement", points: [d.start, d.end], color: d.color, lineWidth: d.lineWidth };
    case "fib_extension":
      return { id: d.id, kind: "fib_extension", points: [d.p0, d.p1, d.p2], color: d.color, lineWidth: d.lineWidth };
    case "cycle":
      return { id: d.id, kind: "cycle", points: [d.anchor, d.second], count: d.count, color: d.color, lineWidth: d.lineWidth };
  }
}

function findNearestDrawing(
  drawings: Array<{ id: string; chartKey: string; kind: string; [key: string]: any }>,
  chartKey: string,
  clickedTimeSeconds: number,
  clickedPrice: number,
  candles: Candle[]
): string | null {
  const onChart = drawings.filter((d) => d.chartKey === chartKey);
  if (onChart.length === 0 || candles.length === 0) return null;
  const range = Math.max(...candles.map((c) => c.high)) - Math.min(...candles.map((c) => c.low));
  const threshold = Math.max(range * 0.01, 0.5);
  let bestId: string | null = null;
  let bestDist = Infinity;
  for (const d of onChart) {
    let dist = Infinity;
    if (d.kind === "horizontal") {
      dist = Math.abs(d.price - clickedPrice);
    } else if (d.kind === "trend" || d.kind === "fib_retracement") {
      const t0 = tsToChartTime(d.start.ts);
      const t1 = tsToChartTime(d.end.ts);
      const tMin = Math.min(t0, t1);
      const tMax = Math.max(t0, t1);
      if (clickedTimeSeconds < tMin || clickedTimeSeconds > tMax) continue;
      const frac = (clickedTimeSeconds - t0) / (t1 - t0 || 1);
      const expected = d.start.price + frac * (d.end.price - d.start.price);
      dist = Math.abs(expected - clickedPrice);
    }
    if (dist < bestDist) { bestDist = dist; bestId = d.id; }
  }
  return bestDist <= threshold ? bestId : null;
}
